import { useState } from 'react';

import { useGetUsersMinFilteredQuery } from '@api/user';
import { UserMin, UserMinListParams } from '@apiContract';
import assoc from 'lodash/fp/assoc';
import omit from 'lodash/fp/omit';

import { xorUsers } from '../../helpers';
import { TabValues, UserMap } from '../../types';

type UseSelectUsersParams = {
  selected?: UserMap;
  deleted?: UserMap;
  filters?: UserMinListParams;
  noSelected?: boolean;
  checkForExistingLocation?: boolean;
};

export const useMultiSelectUsers = ({
  noSelected,
  selected,
  deleted,
  filters,
  checkForExistingLocation,
}: UseSelectUsersParams) => {
  const [selectedUsers, setSelectedUsers] = useState<UserMap>(selected || {});
  const [deletedUsers, setDeletedUsers] = useState<UserMap>(deleted || {});
  const [tempSelected, setTempSelected] = useState<UserMap>({});
  const [activeTab, setActiveTab] = useState<TabValues>(TabValues.SELECTED_USERS);
  const [usersWithLocation, setUsersWithLocation] = useState<UserMin[]>([]);
  const [isLocationConfirmationOpen, setIsLocationConfirmationOpen] = useState(false);

  const { data } = useGetUsersMinFilteredQuery(
    {
      PageNumber: 0,
      ItemsPerPage: 1,
      UserStatus: 1,
      UserRole: 'Recipient',
      ...filters,
    },
    {
      skip: !!noSelected && activeTab === TabValues.SELECTED_USERS,
      refetchOnMountOrArgChange: true,
    },
  );

  const handleTabChange = (newValue: TabValues) => {
    setActiveTab(newValue);
  };

  const onAddUserClick = (user: UserMin) => {
    if (tempSelected[user.id]) {
      setTempSelected(omit([user.id], tempSelected));
    } else {
      setTempSelected(assoc([user.id], user, tempSelected));
    }
  };

  const onRemoveUserClick = (user: UserMin) => {
    if (selectedUsers[user.id]) {
      setSelectedUsers(omit([user.id], selectedUsers));
    } else if (deletedUsers[user.id]) {
      setDeletedUsers(omit([user.id], deletedUsers));
    } else {
      setDeletedUsers(assoc([user.id], user, deletedUsers));
    }
  };

  const applySelectedUsers = () => {
    setSelectedUsers(xorUsers(selectedUsers, tempSelected));
    setTempSelected({});
    handleTabChange(TabValues.SELECTED_USERS);
    setIsLocationConfirmationOpen(false);
  };

  const onApplyUsersClick = () => {
    if (checkForExistingLocation) {
      const usersWithExistingLocation = Object.values(tempSelected).filter(
        (user) => !selectedUsers[user.id] && !!user.locationId,
      );

      if (usersWithExistingLocation.length > 0) {
        setUsersWithLocation(usersWithExistingLocation);
        setIsLocationConfirmationOpen(true);
        return;
      }
    }

    applySelectedUsers();
  };

  const onCancelClick = () => {
    handleTabChange(TabValues.SELECTED_USERS);
    setTempSelected({});
  };

  const hasNewUsersWithLocation = () =>
    Object.values(tempSelected).some((user) => !selectedUsers[user.id] && !!user.locationId);

  return {
    itemsCount: !!noSelected && activeTab === TabValues.SELECTED_USERS ? 0 : data?.totalCount || 0,
    activeTab,
    handleTabChange,
    hasChanges: !!(Object.keys(selectedUsers).length || Object.keys(deletedUsers).length),
    tempSelected,
    selectedUsers,
    onAddUserClick,
    deletedUsers,
    onRemoveUserClick,
    onApplyUsersClick,
    applySelectedUsers,
    onCancelClick,
    usersWithLocation,
    isLocationConfirmationOpen,
    setIsLocationConfirmationOpen,
    hasNewUsersWithLocation,
  };
};
